import React from 'react';
import {
  Box, Card, CardContent, TextField, Select, MenuItem, FormControl,
  IconButton, Tooltip, Switch, FormControlLabel, Divider, Typography,
  Radio, Checkbox, Rating, Button
} from '@mui/material';
import {
  Add, ContentCopy, Delete, AutoAwesome, Close, ShortText, Subject,
  RadioButtonChecked, CheckBox, ArrowDropDown, StarRate, LinearScale,
  CalendarMonth, Numbers, Email, Phone, CloudUpload, AccessTime
} from '@mui/icons-material';

export const QUESTION_TYPES = [
  { value: 'SHORT_TEXT', label: 'Short Answer', icon: <ShortText /> },
  { value: 'LONG_TEXT', label: 'Paragraph', icon: <Subject /> },
  { value: 'MULTIPLE_CHOICE', label: 'Multiple Choice', icon: <RadioButtonChecked /> },
  { value: 'CHECKBOX', label: 'Checkboxes', icon: <CheckBox /> },
  { value: 'DROPDOWN', label: 'Dropdown', icon: <ArrowDropDown /> },
  { value: 'RATING', label: 'Rating', icon: <StarRate /> },
  { value: 'SCALE', label: 'Linear Scale', icon: <LinearScale /> },
  { value: 'DATE', label: 'Date', icon: <CalendarMonth /> },
  { value: 'TIME', label: 'Time', icon: <AccessTime /> },
  { value: 'NUMBER', label: 'Number', icon: <Numbers /> },
  { value: 'EMAIL', label: 'Email', icon: <Email /> },
  { value: 'PHONE', label: 'Phone', icon: <Phone /> },
  { value: 'FILE', label: 'File Upload', icon: <CloudUpload /> },
];

const QuestionEditor = ({ 
  field, 
  index, 
  themeColor, 
  onUpdate, 
  onDelete, 
  onDuplicate, 
  onImproveWithAI,
  aiLoading 
}) => {
  const updateField = (updates) => onUpdate(index, updates);

  const updateOption = (optIndex, value) => {
    const opts = [...(field.options || [])];
    opts[optIndex] = value;
    updateField({ options: opts });
  };

  const removeOption = (optIndex) => {
    updateField({ options: field.options.filter((_, i) => i !== optIndex) });
  };

  const addOption = () => {
    updateField({ options: [...(field.options || []), `Option ${(field.options?.length || 0) + 1}`] });
  };

  const hasOptions = ['MULTIPLE_CHOICE', 'CHECKBOX', 'DROPDOWN'].includes(field.type);

  return (
    <Card sx={{ mb: 2, borderLeft: `4px solid ${themeColor}`, '&:hover': { boxShadow: 3 } }}>
      <CardContent>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            {/* Question Label */}
            <TextField
              fullWidth
              variant="standard"
              placeholder="Question"
              value={field.label || ''}
              onChange={(e) => updateField({ label: e.target.value })}
              sx={{ mb: 1, '& input': { fontSize: '1.1rem', fontWeight: 500 } }}
            />

            {/* Description */}
            <TextField
              fullWidth
              variant="standard"
              placeholder="Description (optional)"
              value={field.description || ''}
              onChange={(e) => updateField({ description: e.target.value })}
              sx={{ mb: 2, '& input': { fontSize: '0.85rem', color: '#666' } }}
            />

            {/* Options for choice-based questions */}
            {hasOptions && (
              <Box sx={{ ml: 2 }}>
                {field.options?.map((opt, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {field.type === 'MULTIPLE_CHOICE' && <Radio disabled size="small" />}
                    {field.type === 'CHECKBOX' && <Checkbox disabled size="small" />}
                    {field.type === 'DROPDOWN' && (
                      <Typography sx={{ mr: 1, color: '#999', minWidth: 20 }}>{i + 1}.</Typography>
                    )}
                    <TextField
                      variant="standard"
                      value={opt}
                      onChange={(e) => updateOption(i, e.target.value)}
                      sx={{ flex: 1 }}
                      placeholder={`Option ${i + 1}`}
                    />
                    {field.options.length > 1 && (
                      <IconButton size="small" onClick={() => removeOption(i)}>
                        <Close fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
                <Button size="small" startIcon={<Add />} onClick={addOption} sx={{ mt: 1 }}>
                  Add Option
                </Button>
              </Box>
            )}

            {/* Scale settings */}
            {field.type === 'SCALE' && (
              <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center' }}>
                <TextField
                  type="number"
                  size="small"
                  label="Min"
                  value={field.settings?.scaleMin || 1}
                  onChange={(e) => updateField({ 
                    settings: { ...field.settings, scaleMin: parseInt(e.target.value) || 1 }
                  })}
                  sx={{ width: 80 }}
                />
                <Typography>to</Typography>
                <TextField
                  type="number"
                  size="small"
                  label="Max"
                  value={field.settings?.scaleMax || 5}
                  onChange={(e) => updateField({ 
                    settings: { ...field.settings, scaleMax: parseInt(e.target.value) || 5 }
                  })}
                  sx={{ width: 80 }}
                />
              </Box>
            )}

            {/* Rating preview */}
            {field.type === 'RATING' && (
              <Rating value={0} disabled sx={{ mt: 1 }} />
            )}
          </Box>

          {/* Question Type Selector */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={field.type}
              onChange={(e) => updateField({
                type: e.target.value,
                options: ['MULTIPLE_CHOICE', 'CHECKBOX', 'DROPDOWN'].includes(e.target.value)
                  ? (field.options?.length ? field.options : ['Option 1', 'Option 2'])
                  : []
              })}
            >
              {QUESTION_TYPES.map(type => (
                <MenuItem key={type.value} value={type.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {type.icon}
                    <Typography variant="body2">{type.label}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Actions */}
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Improve with AI">
            <IconButton 
              size="small" 
              onClick={() => onImproveWithAI(index)} 
              disabled={aiLoading}
              color="primary"
            >
              <AutoAwesome fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Duplicate">
            <IconButton size="small" onClick={() => onDuplicate(index)}>
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => onDelete(index)} color="error">
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={field.required || false}
                onChange={(e) => updateField({ required: e.target.checked })}
              />
            }
            label="Required"
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default QuestionEditor;
